/**
 * Fertilizer product catalog for the Fertilizer Schedule Calculator.
 *
 * Single source of truth for the hub tool (/tools/fertilizer-schedule-calculator)
 * and the per-product landing pages (/tools/milorganite-calculator, etc.).
 *
 * Each product carries the LABEL application rate (the calculator's source of
 * truth), the label bag/coverage claim, a per-product application schedule
 * expressed as month "anchors" (base = northern cool-season calendar; the engine
 * in lib/fertilizer-product-calculator shifts these by the user's region and
 * filters by grass activity), plus SEO metadata + FAQ used by the route folders.
 *
 * Affiliate data (name/price/image/URL) is JOINED to the shared catalog in
 * data/guide-product-recommendations.ts via `productKey`, so the existing
 * PAAPI price-refresh + out-of-stock swap workflow keeps these pages current.
 * A product with no productKey (e.g. Ironite) falls back to an Amazon search URL.
 *
 * Rates are hardcoded from each product's printed label with the verification
 * date in `labelSource`. Always re-verify against the current SKU before edits —
 * Amazon remaps ASINs and manufacturers revise analyses (see the Milorganite
 * 6-4-0 vs 6-2-0 history). "Always follow the bag label" is shown to users.
 */

import type { ProductKey } from '@/data/guide-product-recommendations'

export type FertilizerProductSlug =
  | 'milorganite-calculator'
  | 'scotts-turf-builder-calculator'
  | 'scotts-weed-and-feed-calculator'
  | 'scotts-triple-action-calculator'
  | 'scotts-southern-lawn-food-calculator'
  | 'pgf-complete-calculator'
  | 'lesco-24-0-11-calculator'
  | 'simple-lawn-solutions-calculator'
  | 'ironite-calculator'
  | 'scotts-starter-fertilizer-calculator'

export type GrassSeason = 'cool' | 'warm'

export interface GrassOption {
  id: string
  label: string
  /** Canonical GRASS_CHARACTERISTICS key, for cross-tool prefill. */
  canonical: string
  type: GrassSeason
}

/**
 * A base application window, as a fractional month (1 = Jan 1, 5.7 ≈ late May).
 * `m: 0` is a sentinel for "any time you plant" (starter products only).
 */
export interface ScheduleAnchor {
  m: number
  label: string
}

export interface FertilizerProduct {
  id: string
  slug: FertilizerProductSlug
  /** Join to the shared affiliate catalog. Omit when no Amazon SKU is tracked. */
  productKey?: ProductKey
  /** Exact label name — also the affiliate anchor text (CLAUDE.md rule). */
  name: string
  /** Compact label for the result-card header, e.g. "Milorganite · 6-4-0". */
  short: string
  npk: string
  npkParts: { n: number; p: number; k: number }
  form: 'granular' | 'liquid'
  /** Short chip label, e.g. "Organic", "Weed control". */
  tag: string
  /** Granular: lbs of product per 1,000 sq ft. Liquid: fl oz per 1,000 sq ft. */
  labelRatePer1000: number
  rateLabel: string
  /** Human label for the bag or bottle, e.g. "32 lb / 2,500 sq ft". */
  bagLabel: string
  /** Granular only: bag weight in lbs, for the bags-to-buy count. */
  bagLbs?: number
  /** Liquid only: bottle size in fl oz, for the bottles-to-buy count. */
  containerOz?: number
  maxAppsPerYear: number
  minIntervalWeeks: number
  anchors: ScheduleAnchor[]
  containsWeedControl: boolean
  containsPreEmergent: boolean
  starterOnly: boolean
  /** Methodology-strip citation. */
  labelSource: string
  // SEO / route-folder content
  metaTitle: string
  metaDescription: string
  h1: string
  intro: string
  faq: { question: string; answer: string }[]
}

/**
 * Grass options shown in the calculator. `type` drives the seasonal filtering
 * (cool-season lawns skip summer nitrogen in hot regions; warm-season lawns
 * skip the late-fall window). `canonical` maps back to GRASS_CHARACTERISTICS
 * so grass/ZIP/size can prefill sibling tools.
 */
export const FERTILIZER_GRASS_OPTIONS: GrassOption[] = [
  { id: 'kbg', label: 'Kentucky Bluegrass', canonical: 'Kentucky Bluegrass', type: 'cool' },
  { id: 'fescue', label: 'Tall Fescue', canonical: 'Tall Fescue', type: 'cool' },
  { id: 'rye', label: 'Perennial Rye', canonical: 'Perennial Ryegrass', type: 'cool' },
  { id: 'bermuda', label: 'Bermuda', canonical: 'Bermudagrass', type: 'warm' },
  { id: 'zoysia', label: 'Zoysia', canonical: 'Zoysiagrass', type: 'warm' },
  { id: 'staug', label: 'St. Augustine', canonical: 'St. Augustinegrass', type: 'warm' },
  { id: 'centipede', label: 'Centipede', canonical: 'Centipede', type: 'warm' },
]

export function getGrassOption(id: string): GrassOption | undefined {
  return FERTILIZER_GRASS_OPTIONS.find((g) => g.id === id)
}

export const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/**
 * Region tuning keyed on the region names returned by getRegionFromZipCode
 * (data/regional-climate.ts). `shift` moves every application window later
 * (positive = north/cold) or earlier (negative = south/warm) in fractional
 * months; `hot` marks regions where cool-season lawns must skip summer nitrogen.
 */
export const REGION_TUNING: Record<string, { shift: number; hot: boolean }> = {
  Northeast: { shift: 0.5, hot: false },
  'Great Lakes': { shift: 0.6, hot: false },
  'Upper Midwest': { shift: 0.7, hot: false },
  'Mid-Atlantic': { shift: 0, hot: false },
  Central: { shift: 0, hot: true },
  Southeast: { shift: -0.8, hot: true },
  'South Central': { shift: -1, hot: true },
  'Mountain West': { shift: 0.4, hot: false },
  'West Coast': { shift: -0.2, hot: false },
  'Pacific Northwest': { shift: 0.3, hot: false },
  California: { shift: -0.3, hot: false },
  'Desert Southwest': { shift: -1, hot: true },
  Florida: { shift: -1.2, hot: true },
  Hawaii: { shift: -1.5, hot: true },
  Alaska: { shift: 1, hot: false },
}

export const FERTILIZER_PRODUCTS: FertilizerProduct[] = [
  {
    id: 'milorganite',
    slug: 'milorganite-calculator',
    productKey: 'fertilizer-milorganite',
    name: 'Milorganite 6-4-0',
    short: 'Milorganite · 6-4-0',
    npk: '6-4-0',
    npkParts: { n: 6, p: 4, k: 0 },
    form: 'granular',
    tag: 'Organic',
    labelRatePer1000: 12.8, // 32 lb bag covers 2,500 sq ft = 12.8 lb / 1,000
    rateLabel: '12.8 lbs',
    bagLabel: '32 lb / 2,500 sq ft',
    bagLbs: 32,
    maxAppsPerYear: 4,
    minIntervalWeeks: 8,
    anchors: [
      { m: 5.7, label: 'Around Memorial Day' },
      { m: 7.1, label: 'Around July 4th' },
      { m: 9.0, label: 'Around Labor Day' },
      { m: 11.3, label: 'Around Thanksgiving (dormant feed)' },
    ],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from the Milorganite 32 lb bag label, verified Jun 2026.',
    metaTitle: 'Milorganite Calculator | How Much + When to Apply by ZIP',
    metaDescription:
      'Free Milorganite calculator. Enter your ZIP and lawn size for exactly how much Milorganite per application, how many bags, and your 4-application schedule tuned to your region.',
    h1: 'Milorganite calculator',
    intro:
      "Milorganite's label rate is 12.8 lbs per 1,000 sq ft, and the label caps you at 4 applications a year. Enter your lawn size and ZIP to see the exact pounds per feeding, how many 32 lb bags to buy, and the classic Memorial Day / July 4th / Labor Day / Thanksgiving schedule shifted to your region and grass.",
    faq: [
      {
        question: 'Can I apply Milorganite every month?',
        answer:
          "No. Milorganite's label allows 4 applications per year, roughly 8 weeks apart. The classic schedule is Memorial Day, July 4th, Labor Day, and Thanksgiving. Monthly applications waste product and push past the yearly nitrogen limit.",
      },
      {
        question: 'How much Milorganite do I need for 5,000 sq ft?',
        answer:
          '64 lbs per application, which is 2 of the 32 lb bags. That is the label rate of 12.8 lbs per 1,000 sq ft. The calculator scales this to any lawn size.',
      },
      {
        question: 'When should I apply Milorganite?',
        answer:
          'Cool-season lawns follow the holiday schedule (Memorial Day, July 4th, Labor Day, Thanksgiving). Warm-season lawns feed spring through late summer and skip the dormant-season feeding. Your ZIP shifts each window by up to a month.',
      },
      {
        question: 'Does Milorganite need to be watered in?',
        answer:
          'Watering in helps the slow-release nitrogen and iron start working, but Milorganite will not burn if you skip it. Apply to a dry lawn and water afterward, or let rain do it.',
      },
    ],
  },
  {
    id: 'tb-lawnfood',
    slug: 'scotts-turf-builder-calculator',
    productKey: 'fertilizer-scotts-turf-builder',
    name: 'Scotts Turf Builder Lawn Food',
    short: 'Turf Builder · 32-0-4',
    npk: '32-0-4',
    npkParts: { n: 32, p: 0, k: 4 },
    form: 'granular',
    tag: 'Synthetic',
    labelRatePer1000: 2.5, // 12.5 lb covers 5,000 sq ft = 2.5 lb / 1,000
    rateLabel: '2.5 lbs',
    bagLabel: '12.5 lb / 5,000 sq ft',
    bagLbs: 12.5,
    maxAppsPerYear: 4,
    minIntervalWeeks: 6,
    anchors: [
      { m: 4.5, label: 'Mid spring green-up' },
      { m: 6.2, label: 'Early summer' },
      { m: 9.0, label: 'Early fall' },
      { m: 10.5, label: 'Late fall winterizer' },
    ],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from the Scotts Turf Builder 5,000 sq ft bag label, verified Jun 2026.',
    metaTitle: 'Scotts Turf Builder Calculator | Rate + Schedule by ZIP',
    metaDescription:
      'Free Scotts Turf Builder Lawn Food calculator. Get the exact amount per application, bags needed, and a Scotts fertilizer schedule tuned to your ZIP and grass type.',
    h1: 'Scotts Turf Builder calculator',
    intro:
      "Scotts Turf Builder Lawn Food (32-0-4) goes down at 2.5 lbs per 1,000 sq ft, up to 4 times a year, at least 6 weeks apart. Enter your lawn size and ZIP for the exact pounds per feeding, how many bags to buy, and a Scotts fertilizer schedule that shifts to your region and grass.",
    faq: [
      {
        question: 'What is the Scotts Turf Builder schedule?',
        answer:
          'Four feedings a year is the standard: early spring green-up, late spring/early summer, early fall, and a late-fall winterizer. Keep applications at least 6 weeks apart. The calculator moves each window to your region and drops any that do not fit your grass type.',
      },
      {
        question: 'How much Scotts Turf Builder do I need?',
        answer:
          'The label rate is 2.5 lbs per 1,000 sq ft, so a 5,000 sq ft lawn uses 12.5 lbs (one bag) per application. The calculator scales it to your exact lawn size.',
      },
      {
        question: 'How often can I apply Scotts Turf Builder?',
        answer:
          'Up to 4 times per growing season, at least 6 to 8 weeks apart. More than that risks pushing past the yearly nitrogen limit for your lawn.',
      },
    ],
  },
  {
    id: 'wf3',
    slug: 'scotts-weed-and-feed-calculator',
    productKey: 'weed-feed-scotts',
    name: 'Scotts Turf Builder Weed & Feed',
    short: 'Weed & Feed · 28-0-3',
    npk: '28-0-3',
    npkParts: { n: 28, p: 0, k: 3 },
    form: 'granular',
    tag: 'Weed control',
    labelRatePer1000: 2.86, // ~14.3 lb covers 5,000 sq ft
    rateLabel: '2.9 lbs',
    bagLabel: '14.3 lb / 5,000 sq ft',
    bagLbs: 14.29,
    maxAppsPerYear: 2,
    minIntervalWeeks: 8,
    anchors: [
      { m: 5.0, label: 'Spring, weeds actively growing' },
      { m: 9.3, label: 'Early fall pass' },
    ],
    containsWeedControl: true,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from the Scotts Turf Builder Weed & Feed label, verified Jun 2026.',
    metaTitle: 'Scotts Weed and Feed Calculator | How Much + When to Apply',
    metaDescription:
      'Free Scotts Weed & Feed calculator. Get how much to spread for your lawn size, how many bags, and when to apply for your ZIP, with the damp-grass and overseeding rules.',
    h1: 'Scotts Weed & Feed calculator',
    intro:
      'Scotts Turf Builder Weed & Feed goes down at about 2.9 lbs per 1,000 sq ft, at most twice a year. It follows weed activity, not just grass growth: apply to damp grass with weeds actively growing, and wait 4 weeks before overseeding. Enter your lawn size and ZIP for the amount, bags, and timing.',
    faq: [
      {
        question: 'How much Scotts Weed & Feed do I use?',
        answer:
          'The label rate is about 2.9 lbs per 1,000 sq ft, so a 5,000 sq ft lawn uses roughly one 14.3 lb bag per application. The calculator scales it to your lawn size.',
      },
      {
        question: 'When should I apply Scotts Weed & Feed?',
        answer:
          'Apply when broadleaf weeds are young and actively growing, usually spring, with an optional early-fall pass. Grass should be damp (morning dew or a light watering) so the granules stick to weed leaves. Do not apply more than twice a year.',
      },
      {
        question: 'Can I seed after applying Weed & Feed?',
        answer:
          'No. Wait at least 4 weeks after a weed & feed application before overseeding, and do not apply it to newly seeded lawns until they have been mowed 3 to 4 times.',
      },
    ],
  },
  {
    id: 'triple',
    slug: 'scotts-triple-action-calculator',
    productKey: 'weed-triple-action-scotts',
    name: 'Scotts Turf Builder Triple Action',
    short: 'Triple Action · 25-0-2',
    npk: '25-0-2',
    npkParts: { n: 25, p: 0, k: 2 },
    form: 'granular',
    tag: 'Pre-emergent',
    labelRatePer1000: 2.83, // ~11.3 lb covers 4,000 sq ft
    rateLabel: '2.8 lbs',
    bagLabel: '11.3 lb / 4,000 sq ft',
    bagLbs: 11.31,
    maxAppsPerYear: 2,
    minIntervalWeeks: 8,
    anchors: [
      { m: 3.8, label: 'Early spring, before crabgrass' },
      { m: 9.2, label: 'Early fall' },
    ],
    containsWeedControl: true,
    containsPreEmergent: true,
    starterOnly: false,
    labelSource: 'Rates from the Scotts Turf Builder Triple Action label, verified Jun 2026.',
    metaTitle: 'Scotts Triple Action Calculator | Rate + Spring Timing',
    metaDescription:
      'Free Scotts Triple Action calculator. Get how much to spread, bags needed, and the strict spring pre-emergent timing for your ZIP, plus the no-seeding rule.',
    h1: 'Scotts Triple Action calculator',
    intro:
      'Scotts Triple Action prevents crabgrass, kills broadleaf weeds, and feeds in one pass at about 2.8 lbs per 1,000 sq ft. The spring timing is strict: it has to go down before crabgrass germinates. Enter your lawn size and ZIP for the amount, bags, and your region-specific spring window.',
    faq: [
      {
        question: 'When should I apply Scotts Triple Action?',
        answer:
          'Early spring, before soil temperatures hit 55°F and crabgrass germinates. The pre-emergent only works before weeds sprout, so timing to your region is critical. An optional early-fall pass targets fall weeds.',
      },
      {
        question: 'How much Scotts Triple Action do I need?',
        answer:
          'The label rate is about 2.8 lbs per 1,000 sq ft, so a 4,000 sq ft lawn uses one 11.3 lb bag. The calculator scales it to your lawn size.',
      },
      {
        question: 'Can I seed after using Triple Action?',
        answer:
          'No. Because it contains a pre-emergent, do not seed for about 4 months after applying, and do not use it on lawns seeded in the last year. Use a starter fertilizer for new seed instead.',
      },
    ],
  },
  {
    id: 'southern',
    slug: 'scotts-southern-lawn-food-calculator',
    productKey: 'fertilizer-southern-lawn-food',
    name: 'Scotts Turf Builder Southern Lawn Food',
    short: 'Southern · 32-0-10',
    npk: '32-0-10',
    npkParts: { n: 32, p: 0, k: 10 },
    form: 'granular',
    tag: 'Warm-season',
    labelRatePer1000: 2.81, // ~14 lb covers 5,000 sq ft
    rateLabel: '2.8 lbs',
    bagLabel: '14 lb / 5,000 sq ft',
    bagLbs: 14.06,
    maxAppsPerYear: 4,
    minIntervalWeeks: 6,
    anchors: [
      { m: 4.3, label: 'Spring green-up' },
      { m: 6.0, label: 'Early summer' },
      { m: 7.5, label: 'Mid summer' },
      { m: 9.0, label: 'Early fall, last feed' },
    ],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from the Scotts Turf Builder Southern Lawn Food label, verified Jun 2026.',
    metaTitle: 'Scotts Southern Lawn Food Calculator | Rate + Schedule',
    metaDescription:
      'Free Scotts Southern Lawn Food calculator. Get the amount per application, bags, and a warm-season feeding schedule tuned to your ZIP for Bermuda, Zoysia, St. Augustine, and Centipede.',
    h1: 'Scotts Southern Lawn Food calculator',
    intro:
      'Scotts Southern Lawn Food (32-0-10) is built for warm-season grass and goes down at about 2.8 lbs per 1,000 sq ft. Southern lawns feed spring through late summer and stop before dormancy. Enter your lawn size and ZIP for the amount, bags, and a schedule matched to your region and grass.',
    faq: [
      {
        question: 'When should I feed a Southern lawn?',
        answer:
          'Start at full green-up in spring, feed through summer, and finish with an early-fall application about 6 weeks before dormancy. Warm-season grass cannot use a late-fall feeding, so the calculator drops it.',
      },
      {
        question: 'How much Scotts Southern Lawn Food do I need?',
        answer:
          'The label rate is about 2.8 lbs per 1,000 sq ft, so a 5,000 sq ft lawn uses one 14 lb bag per application. The calculator scales it to your lawn size.',
      },
    ],
  },
  {
    id: 'pgf',
    slug: 'pgf-complete-calculator',
    productKey: 'fertilizer-pgf-complete',
    name: 'The Andersons PGF Complete 16-4-8',
    short: 'PGF Complete · 16-4-8',
    npk: '16-4-8',
    npkParts: { n: 16, p: 4, k: 8 },
    form: 'granular',
    tag: 'Enthusiast',
    labelRatePer1000: 3.6, // 18 lb covers 5,000 sq ft
    rateLabel: '3.6 lbs',
    bagLabel: '18 lb / 5,000 sq ft',
    bagLbs: 18,
    maxAppsPerYear: 6,
    minIntervalWeeks: 4,
    anchors: [
      { m: 4.5, label: 'Mid spring' },
      { m: 5.8, label: 'Late spring' },
      { m: 7.0, label: 'Summer (light)' },
      { m: 8.8, label: 'Late summer' },
      { m: 9.8, label: 'Fall' },
      { m: 10.8, label: 'Late fall' },
    ],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from The Andersons PGF Complete 16-4-8 label, verified Jun 2026.',
    metaTitle: 'PGF Complete Calculator | 16-4-8 Rate + Schedule by ZIP',
    metaDescription:
      'Free PGF Complete 16-4-8 calculator. Get the amount per application, bags, and a season-long feeding schedule for your ZIP and grass type.',
    h1: 'PGF Complete calculator',
    intro:
      "The Andersons PGF Complete (16-4-8) is a fine-particle fertilizer built for frequent light feeding: 3.6 lbs per 1,000 sq ft, up to 6 times a year, about 4 weeks apart. Enter your lawn size and ZIP for the amount, bags, and a full-season schedule tuned to your grass.",
    faq: [
      {
        question: 'How often can I apply PGF Complete?',
        answer:
          'Up to 6 times a year, roughly every 4 weeks during the growing season. Its small particle size is designed for lighter, more frequent feedings than a standard granular fertilizer.',
      },
      {
        question: 'How much PGF Complete do I need?',
        answer:
          'The label rate is 3.6 lbs per 1,000 sq ft, so an 18 lb bag covers 5,000 sq ft per application. The calculator scales it to your lawn size.',
      },
    ],
  },
  {
    id: 'lesco',
    slug: 'lesco-24-0-11-calculator',
    productKey: 'fertilizer-lesco-24-0-11',
    name: 'Lesco 24-0-11',
    short: 'Lesco · 24-0-11',
    npk: '24-0-11',
    npkParts: { n: 24, p: 0, k: 11 },
    form: 'granular',
    tag: 'Pro',
    labelRatePer1000: 4, // 50 lb covers ~12,500 sq ft at this rate
    rateLabel: '4 lbs',
    bagLabel: '50 lb / 12,500 sq ft',
    bagLbs: 50,
    maxAppsPerYear: 4,
    minIntervalWeeks: 6,
    anchors: [
      { m: 4.5, label: 'Mid spring' },
      { m: 6.0, label: 'Early summer' },
      { m: 9.0, label: 'Early fall' },
      { m: 10.6, label: 'Late fall' },
    ],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from the Lesco 24-0-11 professional turf fertilizer label, verified Jun 2026.',
    metaTitle: 'Lesco 24-0-11 Calculator | Rate + Schedule by ZIP',
    metaDescription:
      'Free Lesco 24-0-11 calculator. Get pounds per application, bags for your lawn size, and a feeding schedule tuned to your ZIP and grass type.',
    h1: 'Lesco 24-0-11 calculator',
    intro:
      'Lesco 24-0-11 is a professional-grade turf fertilizer applied at about 4 lbs per 1,000 sq ft. A 50 lb bag covers a lot of lawn, so the bag count is where this tool earns its keep. Enter your lawn size and ZIP for the amount, bags, and your regional schedule.',
    faq: [
      {
        question: 'How much Lesco 24-0-11 do I need?',
        answer:
          'The typical rate is about 4 lbs per 1,000 sq ft, so a 50 lb bag covers roughly 12,500 sq ft per application. The calculator scales it to your lawn size and rounds up to whole bags.',
      },
      {
        question: 'When should I apply Lesco 24-0-11?',
        answer:
          'Cool-season lawns feed in spring and fall and skip peak summer in hot regions; warm-season lawns feed spring through late summer. The calculator sets the windows for your region and grass.',
      },
    ],
  },
  {
    id: 'sls',
    slug: 'simple-lawn-solutions-calculator',
    productKey: 'fertilizer-simple-1648',
    name: 'Simple Lawn Solutions Advanced 16-4-8',
    short: 'SLS 16-4-8 · liquid',
    npk: '16-4-8',
    npkParts: { n: 16, p: 4, k: 8 },
    form: 'liquid',
    tag: 'Liquid',
    labelRatePer1000: 10, // fl oz per 1,000 sq ft
    rateLabel: '10 fl oz',
    bagLabel: '32 oz / 3,200 sq ft',
    containerOz: 32,
    maxAppsPerYear: 6,
    minIntervalWeeks: 4,
    anchors: [
      { m: 4.5, label: 'Mid spring' },
      { m: 5.8, label: 'Late spring' },
      { m: 7.0, label: 'Summer (light)' },
      { m: 8.8, label: 'Late summer' },
      { m: 9.8, label: 'Fall' },
    ],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from the Simple Lawn Solutions Advanced 16-4-8 label, verified Jun 2026.',
    metaTitle: 'Simple Lawn Solutions 16-4-8 Calculator | Liquid Rate by ZIP',
    metaDescription:
      'Free Simple Lawn Solutions 16-4-8 calculator. Get fluid ounces per application, bottles needed, and a liquid feeding schedule for your ZIP and grass.',
    h1: 'Simple Lawn Solutions 16-4-8 calculator',
    intro:
      'Simple Lawn Solutions Advanced 16-4-8 is a liquid lawn food applied at 10 fl oz per 1,000 sq ft through a hose-end sprayer. Because it is liquid, this tool reports fluid ounces and bottles instead of bags. Enter your lawn size and ZIP for the amount and a light, frequent feeding schedule.',
    faq: [
      {
        question: 'How much Simple Lawn Solutions do I need?',
        answer:
          'The label rate is 10 fl oz per 1,000 sq ft, so a 32 oz bottle covers about 3,200 sq ft per application. The calculator reports the fluid ounces and bottles for your lawn size.',
      },
      {
        question: 'How often can I use a liquid lawn fertilizer?',
        answer:
          'Liquid lawn foods are made for lighter, more frequent feeding, roughly every 4 weeks during the growing season, up to 6 times a year.',
      },
    ],
  },
  {
    id: 'ironite',
    slug: 'ironite-calculator',
    // No dedicated Amazon SKU tracked in the shared catalog; falls back to search.
    name: 'Ironite 1-0-1',
    short: 'Ironite · 1-0-1',
    npk: '1-0-1',
    npkParts: { n: 1, p: 0, k: 1 },
    form: 'granular',
    tag: 'Iron supplement',
    labelRatePer1000: 3, // 15 lb covers ~5,000 sq ft
    rateLabel: '3 lbs',
    bagLabel: '15 lb / 5,000 sq ft',
    bagLbs: 15,
    maxAppsPerYear: 4,
    minIntervalWeeks: 4,
    anchors: [
      { m: 5.0, label: 'Spring, with regular feeding' },
      { m: 7.0, label: 'Summer green boost' },
      { m: 9.2, label: 'Early fall' },
    ],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: false,
    labelSource: 'Rates from the Ironite Mineral Supplement 1-0-1 label, verified Jun 2026.',
    metaTitle: 'Ironite Calculator | How Much + When to Apply by ZIP',
    metaDescription:
      'Free Ironite calculator. Get how much Ironite to spread for your lawn size, bags needed, and when to apply for a deep green without pushing growth.',
    h1: 'Ironite calculator',
    intro:
      'Ironite 1-0-1 is an iron supplement, not a fertilizer, so it greens the lawn without forcing a flush of growth. The rate is about 3 lbs per 1,000 sq ft alongside your regular feeding. Enter your lawn size and ZIP for the amount, bags, and timing.',
    faq: [
      {
        question: 'How much Ironite do I need?',
        answer:
          'The label rate is about 3 lbs per 1,000 sq ft, so a 15 lb bag covers roughly 5,000 sq ft. The calculator scales it to your lawn size.',
      },
      {
        question: 'Does Ironite replace fertilizer?',
        answer:
          'No. Ironite is an iron supplement with almost no nitrogen (1-0-1). It deepens color between feedings but does not feed the lawn, so use it alongside a real fertilizer, not instead of one.',
      },
    ],
  },
  {
    id: 'starter',
    slug: 'scotts-starter-fertilizer-calculator',
    productKey: 'starter-scotts',
    name: 'Scotts Turf Builder Starter Food for New Grass',
    short: 'Starter · 24-25-4',
    npk: '24-25-4',
    npkParts: { n: 24, p: 25, k: 4 },
    form: 'granular',
    tag: 'New lawns',
    labelRatePer1000: 3, // 15 lb covers ~5,000 sq ft
    rateLabel: '3 lbs',
    bagLabel: '15 lb / 5,000 sq ft',
    bagLbs: 15,
    maxAppsPerYear: 1,
    minIntervalWeeks: 0,
    anchors: [{ m: 0, label: 'At seeding or sod, any time you plant' }],
    containsWeedControl: false,
    containsPreEmergent: false,
    starterOnly: true,
    labelSource: 'Rates from the Scotts Turf Builder Starter Food label, verified Jun 2026.',
    metaTitle: 'Scotts Starter Fertilizer Calculator | How Much for New Grass',
    metaDescription:
      'Free Scotts Starter fertilizer calculator. Get how much starter food to spread for your new lawn size, bags needed, and how to time it around seeding.',
    h1: 'Scotts Starter fertilizer calculator',
    intro:
      'Scotts Turf Builder Starter Food (24-25-4) is a high-phosphorus feed for new grass, applied once at seeding or sodding at about 3 lbs per 1,000 sq ft. Enter your lawn size for the amount and bags, then switch to a regular fertilizer after 6 to 8 weeks.',
    faq: [
      {
        question: 'How much starter fertilizer do I need?',
        answer:
          'The label rate is about 3 lbs per 1,000 sq ft, so a 15 lb bag covers roughly 5,000 sq ft. The calculator scales it to your lawn size.',
      },
      {
        question: 'When do I apply starter fertilizer?',
        answer:
          'At planting time, right when you seed or lay sod. It is a one-time application; move to a normal lawn fertilizer 6 to 8 weeks later once the grass is established.',
      },
    ],
  },
]

export function getFertilizerProduct(id: string): FertilizerProduct | undefined {
  return FERTILIZER_PRODUCTS.find((p) => p.id === id || p.slug === id)
}

export function getFertilizerProductBySlug(slug: FertilizerProductSlug): FertilizerProduct | undefined {
  return FERTILIZER_PRODUCTS.find((p) => p.slug === slug)
}

export const FERTILIZER_PRODUCT_SLUGS: FertilizerProductSlug[] = FERTILIZER_PRODUCTS.map((p) => p.slug)

export const FERTILIZER_PRODUCT_IDS: string[] = FERTILIZER_PRODUCTS.map((p) => p.id)
